import { FC, useCallback } from 'react';
import { Col, Row } from 'reactstrap';
import { css } from '@emotion/react';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  itinerary_edit_scheduled_ports_onboard,
  ItineraryEditScheduledPortsOnboard,
  ItineraryOnboardType
} from 'views/companies/settings/itinerary-onboard/config';
import { formatBooleanSelectorValue } from '@/store/company-settings/helpers';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Select from '@/library/components/form/inputs/select';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatOptionDisplay } from '../change-tracking';

type ItineraryOnboardProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.itinerary_onboard;
const CATEGORY_TITLE = 'Itinerary Onboard';

const YES_NO_OPTIONS = [
  { label: 'No', value: '0' },
  { label: 'Yes', value: '1' }
];

const LABELS = {
  [itinerary_edit_scheduled_ports_onboard]:
    'Edit itinerary scheduled port from the onboard application'
};

const ItineraryOnboard: FC<ItineraryOnboardProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<ItineraryOnboardType> = rest.fields;

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [itinerary_edit_scheduled_ports_onboard]:
        companySettings.itinerary_edit_scheduled_ports_onboard
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as ItineraryOnboardType | null,
    toPayload: values => [
      {
        key: itinerary_edit_scheduled_ports_onboard,
        value: formatBooleanSelectorValue(values[itinerary_edit_scheduled_ports_onboard])
      }
    ],
    labels: LABELS,
    formatValue: (_key, value) => formatOptionDisplay(value, YES_NO_OPTIONS),
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <Row
        className="d-flex align-items-center gx-1"
        css={css`
          .form-field {
            width: 8.5rem;
          }
        `}
      >
        <Col>
          <VisibleSetting
            categoryId={CATEGORY_ID}
            settingKey={itinerary_edit_scheduled_ports_onboard}
          >
            <Select
              className="mb-0"
              label={
                <InputLabel
                  title="EDIT ITINERARY SCHEDULED PORT FROM THE ONBOARD APPLICATION"
                  apiKey={itinerary_edit_scheduled_ports_onboard}
                />
              }
              options={YES_NO_OPTIONS}
              onChange={(option: ItineraryEditScheduledPortsOnboard) =>
                selectField(itinerary_edit_scheduled_ports_onboard)(option)
              }
              {...fields[itinerary_edit_scheduled_ports_onboard]}
            />
          </VisibleSetting>
        </Col>
      </Row>
    </Category>
  );
};

export default ItineraryOnboard;
